import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradePrompt } from '../components/Premium/UpgradePrompt';
import { Plus, Image, X } from 'lucide-react';
import { useMoodBoard } from '../hooks/useMoodBoard';

export function MoodBoard() {
  const { hasFeature, showUpgradePrompt } = useSubscription();
  const { boards, loading, error, createBoard } = useMoodBoard();
  const isPremium = hasFeature('mood_board');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreateBoard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBoardName.trim()) return;

    try {
      await createBoard(newBoardName.trim());
      setShowCreateModal(false);
      setNewBoardName('');
      setCreateError(null);
    } catch (err) {
      console.error('Error creating board:', err);
      setCreateError('Failed to create board. Please try again.');
    }
  };

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <UpgradePrompt
            feature="Mood Board"
            description="Create beautiful mood boards to visualize your ideas and inspiration. Organize images, colors, and notes in a creative space."
            onUpgrade={() => showUpgradePrompt('mood_board')}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Quick Add Button */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="fixed bottom-24 right-6 md:bottom-8 w-14 h-14 bg-[var(--accent-color)] rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity z-50"
      >
        <Plus className="w-6 h-6 text-black" />
      </button>

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Mood Board</h1>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--accent-color)] border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-400">Loading mood boards...</p>
          </div>
        ) : boards.length === 0 ? (
          <div className="text-center py-12">
            <Image className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-medium mb-2">No mood boards yet</h2>
            <p className="text-gray-400 mb-6">
              Create your first mood board to start collecting inspiration
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {boards.map(board => (
              <div
                key={board.id}
                className="bg-[#141414] rounded-xl cursor-pointer hover:ring-1 hover:ring-[var(--accent-color)] transition-all p-2"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium">{board.name}</h3>
                </div>
                <div className="aspect-video bg-black/20 mb-4 flex items-center justify-center">
                  {board.images && board.images.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 p-2 w-full h-full">
                      {board.images.slice(0, 4).map((image) => (
                        <div
                          key={image.id}
                          className="overflow-hidden bg-black/10"
                        >
                          <img
                            src={image.url}
                            alt={image.caption || ''}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Image className="w-8 h-8 text-gray-500" />
                  )}
                </div>
                <div className="text-sm text-gray-400">
                  {board.images?.length || 0} images
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Board Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-[#141414] rounded-3xl p-6 max-w-md w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">Create New Board</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-black/20 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateBoard} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Board Name
                  </label>
                  <input
                    type="text"
                    value={newBoardName}
                    onChange={(e) => setNewBoardName(e.target.value)}
                    className="w-full px-4 py-2 bg-black/20 rounded-xl text-white"
                    required
                    placeholder="e.g., Project Inspiration"
                  />
                </div>

                {createError && (
                  <div className="p-3 bg-red-500/10 text-red-500 rounded-xl text-sm">
                    {createError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
                >
                  Create Board
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}